/**
 * Tab containment for `aria-modal` dialogs.
 *
 * `aria-modal="true"` tells assistive tech the rest of the page is inert. Nothing enforces
 * that — without a trap, Tab walks straight out of the dialog and into content a screen
 * reader has just been told does not exist. Shared so every dialog contains focus the same
 * way rather than each one re-deriving the first/last cycling.
 */

/**
 * Elements Tab can reach. Kept deliberately simple — dialog content in this app is a short
 * button row plus a handful of inputs.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Whether Tab can land on this element. Same rule as `getFocusableElements`, for a single element
 * that is not inside the dialog — the guided tour adds the control a step asks the coach to press
 * to its Tab ring, and a control that cannot take focus must not become a dead stop in it.
 */
export const isFocusableElement = (element: HTMLElement): boolean =>
  element.matches(FOCUSABLE_SELECTOR) &&
  !element.hasAttribute('disabled') &&
  element.getAttribute('aria-hidden') !== 'true';

export const getFocusableElements = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isFocusableElement);

/**
 * Keeps a Tab press inside `dialog`, wrapping at either end. Call from a keydown handler
 * that has already established the event is a Tab.
 *
 * Calls `preventDefault()` itself when it moves focus, and leaves the event alone when the
 * browser's own tab order would have stayed inside the dialog anyway.
 */
export const cycleTabFocus = (dialog: HTMLElement, event: KeyboardEvent): void => {
  const focusable = getFocusableElements(dialog);
  const active = document.activeElement;

  if (focusable.length === 0) {
    event.preventDefault();
    dialog.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  // Focus has left the dialog (or sits on the dialog itself and is about to leave
  // backwards) — pull it back to the near edge.
  if (!(active instanceof HTMLElement) || !dialog.contains(active)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }

  if (event.shiftKey && (active === first || active === dialog)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};
